'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  program: string;
  experience: string;
}

interface EnrollmentFormProps {
  onClose?: () => void;
  title?: string;
  subtitle?: string;
}

const EnrollmentForm = ({ 
  onClose, 
  title = 'Start Your Learning Journey',
  subtitle = 'Fill in your details to get the free consultation and program recommendations.'
}: EnrollmentFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    program: '',
    experience: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.program) {
      newErrors.program = 'Please select a program';
    }

    if (!formData.experience) {
      newErrors.experience = 'Please select your experience level';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/submit.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Submit failed with status ${response.status}`);
      }
      
      console.log('Form submitted:', formData);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          program: '',
          experience: '',
        });
        setSubmitSuccess(false);
        setErrors({});
        onClose?.();
      }, 5500);
    } catch (error) {
      console.error('Failed to submit form:', error);
      setErrors({ submit: 'Failed to submit form. Please try again.' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-6">
        <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground font-body">
          {subtitle}
        </p>
      </div>

      {submitSuccess ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto">
              <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-success" />
            </div>
            <h4 className="text-xl font-headline font-bold text-foreground">
              Thank You!
            </h4>
            <p className="text-muted-foreground font-body">
              We'll get back to you shortly with personalized program recommendations.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col space-y-4">
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}
          <div>
            <label htmlFor="fullName" className="block text-sm font-body font-medium text-foreground mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 bg-background border rounded-lg text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                errors.fullName ? 'border-red-500' : 'border-primary/20'
              }`}
              placeholder="Enter your full name"
            />
            {errors.fullName && <p className="text-sm text-red-500 mt-1">{errors.fullName}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-body font-medium text-foreground mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 bg-background border rounded-lg text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                errors.email ? 'border-red-500' : 'border-primary/20'
              }`}
              placeholder="your.email@example.com"
            />
            {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-body font-medium text-foreground mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 bg-background border rounded-lg text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                errors.phone ? 'border-red-500' : 'border-primary/20'
              }`}
              placeholder="+1 (555) 000-0000"
            />
            {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="program" className="block text-sm font-body font-medium text-foreground mb-2">
              Program of Interest *
            </label>
            <select
              id="program"
              name="program"
              value={formData.program}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 bg-background border rounded-lg text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                errors.program ? 'border-red-500' : 'border-primary/20'
              }`}
            >
              <option value="">Select a program</option>
              <option value="full-stack">Full Stack Development</option>
              <option value="data-science">Data Science</option>
              <option value="ui-ux">UI/UX Design</option>
              <option value="digital-marketing">Digital Marketing</option>
              <option value="cloud-computing">Cloud Computing</option>
              <option value="cybersecurity">Cybersecurity</option>
            </select>
            {errors.program && <p className="text-sm text-red-500 mt-1">{errors.program}</p>}
          </div>

          <div>
            <label htmlFor="experience" className="block text-sm font-body font-medium text-foreground mb-2">
              Experience Level *
            </label>
            <select
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 bg-background border rounded-lg text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${
                errors.experience ? 'border-red-500' : 'border-primary/20'
              }`}
            >
              <option value="">Select your level</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="professional">Professional</option>
            </select>
            {errors.experience && <p className="text-sm text-red-500 mt-1">{errors.experience}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-auto px-6 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-base shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Get Started
                <Icon name="ArrowRightIcon" size={20} variant="outline" className="ml-2" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default EnrollmentForm;
